/**
 * @fileoverview Input/output schemas for scenes-rebuilder-v1.
 *
 * This agent refreshes the scene breakdown for a chapter based on actual written content.
 * It receives existing scenes as reference but is free to restructure them entirely —
 * adding, removing, merging, or splitting scenes to match what was actually written.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Full input for the agent flow.
 */
export const InputSchema = z.object({
  ...BaseFlowInputSchema.shape,
  ...AGENT_SCHEMA_OBJECTS.chapterContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.chapterContent.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.scenes.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.knownCharacters.schema.shape,
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * Schema for a refreshed scene. These are the complete new scenes for the chapter.
 * Scenes may carry an `oldSceneId` if they correspond to a previous scene.
 */
export const RefreshedSceneSchema = z.object({
  oldSceneId: z.string().optional().describe(
    'The ID of the original scene this corresponds to, if it is a continuation or update of an existing scene. Omit for newly added scenes.'
  ),
  sceneNumber: z.number().describe('The scene number within the chapter, starting from 1'),
  title: z.string().describe('A compelling title for the scene'),
  keyBeats: z.string().describe('Key beats reflecting what actually happens in this scene'),
  projectedWordCount: z.number().describe('Estimated word count for this scene based on actual content'),
  characterIds: z.array(z.string()).describe('IDs of characters that actually appear in this scene'),
});

/**
 * Output schema for scenes-rebuilder-v1.
 */
export const OutputSchema = z.object({
  justification: z.string().describe(
    'Explanation of what structural changes were made to the scene breakdown and why. ' +
    'Note which scenes were kept, added, removed, merged, or split. 2-6 sentences.'
  ),
  refreshedScenes: z.array(RefreshedSceneSchema).describe(
    'The complete new set of scenes for this chapter. This replaces the old scene list entirely.'
  ),
});

export type Output = z.infer<typeof OutputSchema>;
export type RefreshedScene = z.infer<typeof RefreshedSceneSchema>;
